'use client';

import { useState, useEffect, useRef } from 'react';
import { BellIcon } from '@heroicons/react/24/outline';
import { notificationService } from '../../services/notificationService';
import { Notification } from '../../types/database.types';
import { useAuth } from '../../contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { supabase } from '../../lib/supabase';

let channelSequence = 0;

export default function NotificationDropdown() {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const router = useRouter();

    const userId = user?.id;
    const unreadCount = notifications.filter(n => !n.is_read).length;

    useEffect(() => {
        if (!userId) {
            setNotifications([]);
            return;
        }

        let active = true;

        (async () => {
            try {
                const data = await notificationService.getNotifications(userId);
                if (active) setNotifications(data);
            } catch (error) {
                console.error('Failed to load notifications', error);
            }
        })();

        const channel = supabase
            .channel(`notifications:${userId}:${channelSequence++}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'notifications',
                    filter: `user_id=eq.${userId}`
                },
                (payload) => {
                    if (payload.eventType === 'INSERT') {
                        const created = payload.new as Notification;
                        setNotifications(prev =>
                            prev.some(n => n.id === created.id) ? prev : [created, ...prev]
                        );
                    } else if (payload.eventType === 'UPDATE') {
                        const updated = payload.new as Notification;
                        setNotifications(prev =>
                            prev.map(n => (n.id === updated.id ? updated : n))
                        );
                    } else if (payload.eventType === 'DELETE') {
                        const deletedId = (payload.old as Partial<Notification>).id;
                        setNotifications(prev => prev.filter(n => n.id !== deletedId));
                    }
                }
            )
            .subscribe();

        return () => {
            active = false;
            supabase.removeChannel(channel);
        };
    }, [userId]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleMarkAsRead = async (id: string, event: React.MouseEvent) => {
        event.stopPropagation();
        try {
            await notificationService.markAsRead(id);
            // Real-time subscription will handle the state update
        } catch (error) {
            console.error('Failed to mark as read', error);
        }
    };

    const handleDelete = async (id: string, event: React.MouseEvent) => {
        event.stopPropagation();
        try {
            await notificationService.deleteNotification(id);
            // Real-time subscription will handle the state update
        } catch (error) {
            console.error('Failed to delete notification', error);
        }
    };

    const handleNotificationClick = async (notification: Notification) => {
        if (!notification.is_read) {
            try {
                await notificationService.markAsRead(notification.id);
                // Real-time subscription will handle the state update
            } catch (error) {
                console.error('Failed to mark as read', error);
            }
        }

        if (notification.type === 'admin_alert' && notification.related_entity_type === 'adoption_inquiry') {
            router.push('/admin-application-review');
        } else if (notification.type === 'application_update' && notification.related_entity_id) {
            // Navigate to application status tracking
            router.push(`/application-status-tracking/${notification.related_entity_id}`);
        }
        setIsOpen(false);
    };

    if (!user) return null;

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none"
            >
                <BellIcon className="h-6 w-6" />
                {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 h-2.5 w-2.5 bg-red-500 rounded-full border-2 border-white"></span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg py-1 z-50 border border-gray-100 max-h-96 overflow-y-auto">
                    <div className="px-4 py-2 border-b border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                    </div>

                    {notifications.length === 0 ? (
                        <div className="px-4 py-6 text-center text-gray-500 text-sm">
                            No notifications
                        </div>
                    ) : (
                        <div>
                            {notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification)}
                                    className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0 ${!notification.is_read ? 'bg-blue-50' : ''
                                        }`}
                                >
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1 min-w-0 pr-2">
                                            <p className={`text-sm font-medium ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                                                {notification.title}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                                {notification.message}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                {new Date(notification.created_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div className="flex flex-col space-y-1">
                                            {!notification.is_read && (
                                                <button
                                                    onClick={(e) => handleMarkAsRead(notification.id, e)}
                                                    className="text-xs text-blue-600 hover:text-blue-800"
                                                    title="Mark as read"
                                                >
                                                    Read
                                                </button>
                                            )}
                                            <button
                                                onClick={(e) => handleDelete(notification.id, e)}
                                                className="text-xs text-red-600 hover:text-red-800"
                                                title="Delete"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
